import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Target, Edit, ChevronDown, ChevronUp } from 'lucide-react';
import { Checkpoint } from '../../../types/checkpoint';
import { checkpointService } from '../../../services/checkpointService';
import projectTypeService, { ProjectType } from '../../../services/projectTypeService';
import { assetService } from '../../../services/assetService';
import Button from '../../../shared/components/ui/button/Button';

interface CheckpointDetailsDrawerProps {
  checkpointId: string | null;
  open: boolean;
  onClose: () => void;

}

// Modern Clean Brand Colors - Matching existing components
const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  primarySoft: "#EDF7F2",
  accent: "#A3CD39",
  dark: "#007F66",
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

// Frequency color mapping
const FREQUENCY_COLORS = {
  Daily: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  Weekly: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500' },
  Monthly: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
  Quarterly: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
  'Half-Yearly': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  Yearly: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', dot: 'bg-indigo-500' },
};

const formatDate = (dateString: string | Date) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatRelativeTime = (dateString: string | Date) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return `${Math.floor(diffInDays / 365)} years ago`;
};

export default function CheckpointDetailsDrawer({
  checkpointId,
  open,
  onClose
}: CheckpointDetailsDrawerProps) {
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedEdit, setExpandedEdit] = useState<string | null>(null);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [assets, setAssets] = useState<any[]>([]);
  const [editForm, setEditForm] = useState<{
    name: string;
    projectTypeId: string;
    assetId: string;
    frequency: 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly' | '';
    description: string;
    taskType: ('Visual' | 'Hearing' | 'Manual')[];
    isImageRequired: boolean;
    isInputRequired: 'None' | 'Number' | 'Text' | 'Both';
    executionPhase: string;
    executionTime: string;
    requirement: string;
  }>({
    name: '',
    projectTypeId: '',
    assetId: '',
    frequency: '',
    description: '',
    taskType: [],
    isImageRequired: false,
    isInputRequired: 'None',
    executionPhase: '',
    executionTime: '',
    requirement: ''
  });

  useEffect(() => {
    if (open && checkpointId) {
      fetchCheckpointDetails();
      fetchProjectTypes();
    }
  }, [open, checkpointId]);

  const fetchCheckpointDetails = async () => {
    if (!checkpointId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await checkpointService.getCheckpointById(checkpointId);
      // Handle both single checkpoint and array of checkpoints
      if (Array.isArray(response.data)) {
        setCheckpoints(response.data);
      } else {
        setCheckpoints([response.data]);
      }
    } catch (err: any) {
      console.error('Failed to fetch checkpoint details:', err);
      setError(err.message || 'Failed to fetch checkpoint details');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectTypes = async () => {
    try {
      const response = await projectTypeService.getAllProjectTypes();
      // getAllProjectTypes returns ProjectType[] directly, not wrapped in success/data
      setProjectTypes(response);
    } catch (err) {
      console.error('Failed to fetch project types:', err);
    }
  };

  const fetchAssetsByProjectType = async (projectTypeId: string) => {
    try {
      const response = await assetService.getAssets({ projectTypeId });
      // getAssets returns { assets: Asset[], totalDocs: number, pagination?: any }
      setAssets(response.assets || []);
    } catch (err) {
      console.error('Failed to fetch assets:', err);
    }
  };

  const handleEditClick = (checkpoint: Checkpoint) => {
    const checkpointId = checkpoint.id || checkpoint._id;
    setExpandedEdit(expandedEdit === checkpointId ? null : checkpointId);
    setEditForm({
      name: checkpoint.name,
      projectTypeId: typeof checkpoint.projectTypeId === 'object' ? checkpoint.projectTypeId.id || checkpoint.projectTypeId._id : checkpoint.projectTypeId,
      assetId: typeof checkpoint.assetId === 'object' ? checkpoint.assetId.id || checkpoint.assetId._id : checkpoint.assetId,
      frequency: (checkpoint.frequency as 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly') || '',
      description: checkpoint.description || '',
      taskType: checkpoint.taskType || [],
      isImageRequired: checkpoint.isImageRequired || false,
      isInputRequired: (checkpoint.isInputRequired as any) === true ? 'Number' : (checkpoint.isInputRequired as any) || 'None',
      executionPhase: checkpoint.executionPhase || '',
      executionTime: checkpoint.executionTime || '',
      requirement: checkpoint.requirement || ''
    });

    // Fetch assets for the selected project type
    if (checkpoint.projectTypeId) {
      const projectTypeId = typeof checkpoint.projectTypeId === 'object' ? checkpoint.projectTypeId.id || checkpoint.projectTypeId._id : checkpoint.projectTypeId;
      fetchAssetsByProjectType(projectTypeId);
    }
  };

  const handleProjectTypeChange = (projectTypeId: string) => {
    setEditForm(prev => ({ ...prev, projectTypeId, assetId: '' }));
    setAssets([]);
    if (projectTypeId) {
      fetchAssetsByProjectType(projectTypeId);
    }
  };

  const handleSaveEdit = async (checkpoint: Checkpoint) => {
    try {
      const checkpointId = checkpoint.id || checkpoint._id;
      await checkpointService.updateCheckpoint(checkpointId, {
        ...editForm,
        frequency: editForm.frequency || undefined,
        description: editForm.description || undefined,
        taskType: editForm.taskType.length > 0 ? editForm.taskType : undefined,
        isImageRequired: editForm.isImageRequired,
        isInputRequired: editForm.isInputRequired,
        executionPhase: editForm.executionPhase || undefined,
        executionTime: editForm.executionTime || undefined,
        requirement: editForm.requirement || undefined
      });
      setExpandedEdit(null);
      fetchCheckpointDetails(); // Refresh data
    } catch (err: any) {
      console.error('Failed to update checkpoint:', err);
      setError(err.message || 'Failed to update checkpoint');
    }
  };

  const getFrequencyColor = (freq: string) => {
    return FREQUENCY_COLORS[freq as keyof typeof FREQUENCY_COLORS] ||
      { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-500' };
  };

  const getRoleName = (user: any) => {
    if (user && user.role) {
      // If role is an object, extract the name
      if (typeof user.role === 'object' && user.role.name) {
        return user.role.name;
      }
      // If role is a string ID, you might want to fetch role details
      return 'User'; // Default fallback
    }
    return 'N/A';
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-9999 bg-black/40 bg-opacity-30"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
            className="fixed inset-y-0 right-0 z-9999 w-full max-w-4xl overflow-y-auto bg-white shadow-xl"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND_COLORS.primary}20` }}>
                    <Target size={24} style={{ color: BRAND_COLORS.primary }} />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Checkpoint Details</h2>
                    <p className="text-sm text-gray-600">
                      {checkpoints.length > 0 ? `${checkpoints.length} checkpoints` : 'Loading...'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                  aria-label="Close drawer"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 p-6 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: BRAND_COLORS.primary }}></div>
                  </div>
                ) : error ? (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    {error}
                  </div>
                ) : checkpoints.length > 0 ? (
                  <div className="space-y-6">
                    {/* Checkpoints Table */}
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Checkpoints</h3>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                                Sr No
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                                Checkpoint Name
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                                Frequency
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                                Created By
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                                Updated By
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                                Updated At
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {checkpoints.map((checkpoint, index) => (
                              <React.Fragment key={checkpoint.id || checkpoint._id}>
                                <tr className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {index + 1}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {checkpoint.name}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {checkpoint.frequency && (
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getFrequencyColor(checkpoint.frequency).bg
                                        } ${getFrequencyColor(checkpoint.frequency).text} ${getFrequencyColor(checkpoint.frequency).border
                                        }`}>
                                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${getFrequencyColor(checkpoint.frequency).dot
                                          }`}></span>
                                        {checkpoint.frequency}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <div>
                                      <div className="font-medium">
                                        {typeof checkpoint.createdBy === 'object' && checkpoint.createdBy
                                          ? checkpoint.createdBy.name
                                          : 'N/A'}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {typeof checkpoint.createdBy === 'object' && checkpoint.createdBy
                                          ? getRoleName(checkpoint.createdBy)
                                          : 'N/A'}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <div>
                                      <div className="font-medium">
                                        {typeof checkpoint.updatedBy === 'object' && checkpoint.updatedBy
                                          ? checkpoint.updatedBy.name
                                          : 'N/A'}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {typeof checkpoint.updatedBy === 'object' && checkpoint.updatedBy
                                          ? getRoleName(checkpoint.updatedBy)
                                          : 'N/A'}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <div>
                                      <div className="text-xs">
                                        {formatDate(checkpoint.updatedAt)}
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        {formatRelativeTime(checkpoint.updatedAt)}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <Button
                                      onClick={() => handleEditClick(checkpoint)}
                                      variant="outline"
                                      size="sm"
                                      className="flex items-center gap-2"
                                    >
                                      {expandedEdit === (checkpoint.id || checkpoint._id) ? (
                                        <ChevronUp size={16} />
                                      ) : (
                                        <ChevronDown size={16} />
                                      )}
                                      Edit
                                    </Button>
                                  </td>
                                </tr>

                                {/* Expanded Edit Form */}
                                {expandedEdit === (checkpoint.id || checkpoint._id) && (
                                  <tr>
                                    <td colSpan={7} className="px-6 py-4 bg-gray-50">
                                      <div className="space-y-4">
                                        <h4 className="font-medium text-gray-900">Edit Checkpoint 2</h4>

                                        <div className="grid grid-cols-2 gap-4">
                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Project Type
                                            </label>
                                            <select
                                              value={editForm.projectTypeId}
                                              onChange={(e) => handleProjectTypeChange(e.target.value)}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                                              style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                                            >
                                              <option value="">Select Project Type</option>
                                              {projectTypes.map((pt) => (
                                                <option key={pt._id} value={pt._id}>
                                                  {pt.name}
                                                </option>
                                              ))}
                                            </select>
                                          </div>

                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Asset
                                            </label>
                                            <select
                                              value={editForm.assetId}
                                              onChange={(e) => setEditForm(prev => ({ ...prev, assetId: e.target.value }))}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                                              style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                                              disabled={!editForm.projectTypeId}
                                            >
                                              <option value="">Select Asset</option>
                                              {assets.map((asset) => (
                                                <option key={asset._id} value={asset._id}>
                                                  {asset.name}
                                                </option>
                                              ))}
                                            </select>
                                          </div>

                                          <div className="col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Checkpoint Name
                                            </label>
                                            <input
                                              type="text"
                                              value={editForm.name}
                                              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                                              style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                                            />
                                          </div>

                                          <div className="col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Description
                                            </label>
                                            <textarea
                                              value={editForm.description}
                                              onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                                              style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                                              rows={2}
                                            />
                                          </div>

                                          <div className="col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Requirement
                                            </label>
                                            <textarea
                                              value={editForm.requirement}
                                              onChange={(e) => setEditForm(prev => ({ ...prev, requirement: e.target.value }))}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                                              style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                                              rows={2}
                                            />
                                          </div>

                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Frequency
                                            </label>
                                            <select
                                              value={editForm.frequency}
                                              onChange={(e) => setEditForm(prev => ({ ...prev, frequency: e.target.value as any }))}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                                              style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                                            >
                                              <option value="">Select Frequency</option>
                                              <option value="Daily">Daily</option>
                                              <option value="Weekly">Weekly</option>
                                              <option value="Fortnightly">Fortnightly</option>
                                              <option value="Monthly">Monthly</option>
                                              <option value="Quarterly">Quarterly</option>
                                              <option value="Half-Yearly">Half-Yearly</option>
                                              <option value="Yearly">Yearly</option>
                                            </select>
                                          </div>

                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Task Type
                                            </label>
                                            <div className="flex gap-2 flex-wrap">
                                              {['Visual', 'Hearing', 'Manual'].map((type) => (
                                                <label key={type} className="inline-flex items-center">
                                                  <input
                                                    type="checkbox"
                                                    checked={editForm.taskType.includes(type as any)}
                                                    onChange={(e) => {
                                                      const newTypes = e.target.checked
                                                        ? [...editForm.taskType, type as any]
                                                        : editForm.taskType.filter((t) => t !== type);
                                                      setEditForm(prev => ({ ...prev, taskType: newTypes }));
                                                    }}
                                                    className="rounded border-gray-300 text-primary focus:ring-primary"
                                                  />
                                                  <span className="ml-2 text-sm text-gray-700">{type}</span>
                                                </label>
                                              ))}
                                            </div>
                                          </div>

                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Input Required
                                            </label>
                                            <select
                                              value={editForm.isInputRequired}
                                              onChange={(e) => setEditForm(prev => ({ ...prev, isInputRequired: e.target.value as any }))}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                                              style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                                            >
                                              <option value="None">None</option>
                                              <option value="Number">Number</option>
                                              <option value="Text">Text</option>
                                              <option value="Both">Both</option>
                                            </select>
                                          </div>

                                          <div className="flex items-center pt-6">
                                            <label className="inline-flex items-center">
                                              <input
                                                type="checkbox"
                                                checked={editForm.isImageRequired}
                                                onChange={(e) => setEditForm(prev => ({ ...prev, isImageRequired: e.target.checked }))}
                                                className="rounded border-gray-300 text-primary focus:ring-primary"
                                              />
                                              <span className="ml-2 text-sm text-gray-700">Image Required</span>
                                            </label>
                                          </div>

                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Execution Phase
                                            </label>
                                            <input
                                              type="text"
                                              value={editForm.executionPhase}
                                              onChange={(e) => setEditForm(prev => ({ ...prev, executionPhase: e.target.value }))}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                                              style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                                              placeholder="e.g., Morning Shift"
                                            />
                                          </div>

                                          <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                              Execution Time
                                            </label>
                                            <input
                                              type="text"
                                              value={editForm.executionTime}
                                              onChange={(e) => setEditForm(prev => ({ ...prev, executionTime: e.target.value }))}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                                              style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                                              placeholder="e.g., 09:00 - 17:00"
                                            />
                                          </div>
                                        </div>

                                        <div className="flex gap-3">
                                          <Button
                                            onClick={() => handleSaveEdit(checkpoint)}
                                            variant="default"
                                            size="sm"
                                          >
                                            Save Changes
                                          </Button>
                                          <Button
                                            onClick={() => setExpandedEdit(null)}
                                            variant="outline"
                                            size="sm"
                                          >
                                            Cancel
                                          </Button>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    No checkpoint data available
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
